import { getSupabase } from '@/lib/supabase';
import { createRow, describeError, listRows } from '@/api/resources';

/**
 * Uploading a document, and reading one back.
 *
 * Two things are stored, and both are needed: the FILE in a private bucket, and a
 * `doc.documents` ROW describing it. The row is what everything else points at - a KYC record,
 * a farmer's photograph, a consent form - and it is what carries the organisation, the
 * verification state and the audit trail. A file with no row is invisible to the application;
 * a row with no file is a broken link.
 *
 * The bucket is private, so nothing here ever produces a lasting URL. Viewing a file means
 * asking for a signed one that expires.
 */

import { bucketRefusal, type BucketId } from '@/lib/bucketRules';

export { BUCKET_RULES, bucketRefusal, type BucketId } from '@/lib/bucketRules';

export interface UploadedDocument {
  id: string;
  storage_bucket: string;
  storage_path: string;
  original_filename: string;
  mime_type: string;
  file_size_bytes: number;
}

/**
 * The first folder segment is the organisation, and it is what the storage policy compares
 * against the caller's own. It is read from the signed-in profile rather than passed in, so a
 * screen cannot accidentally write into another tenant's folder.
 */
function keyFor(organizationId: string, ownerId: string, documentId: string, filename: string): string {
  const dot = filename.lastIndexOf('.');
  const extension = dot > 0 ? filename.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '') : 'bin';
  return `${organizationId}/${ownerId}/${documentId}.${extension}`;
}

/** sha256 of the bytes, so a document can be shown to be the one that was uploaded. */
async function digest(file: File): Promise<string | null> {
  // crypto.subtle needs a secure context: present on https and on localhost, absent if the app
  // is ever served over plain http from another host. The hash is evidence, not a requirement.
  if (!globalThis.crypto?.subtle) return null;
  const buffer = await file.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function uploadDocument(input: {
  file: File;
  bucket: BucketId;
  organizationId: string;
  /** The party, farmer or plot the file belongs to. Only used to keep the path readable. */
  ownerId: string;
  documentTypeId?: string | null;
  captureMethod?: 'upload' | 'camera' | 'scanner' | 'generated' | 'import';
  issuedDate?: string | null;
  expiryDate?: string | null;
}): Promise<UploadedDocument> {
  const supabase = getSupabase();

  // The row is created FIRST, so the file is named after a real document id and an interrupted
  // upload leaves a row pointing at nothing rather than a file nothing points at. An orphan row
  // is visible and fixable; an orphan file in a private bucket is neither.
  // The same file cannot be two documents (the table holds its hash unique). Checked BEFORE the
  // upload, so a repeat does not leave an orphan copy in storage and the person is told why.
  const refusal = bucketRefusal(input.bucket, input.file);
  if (refusal) throw new Error(refusal);

  // Asked of the server, not read from `documents`: that read is limited to the rows the caller
  // may see, so a repeat attached to a record outside their scope slipped past, was uploaded,
  // and was then refused by the unique index - an orphan file and an unexplained error.
  const hash = await digest(input.file);
  if (hash) {
    const { data: taken } = await supabase.rpc('fn_document_hash_in_use' as never, { p_sha256: hash } as never);
    if (taken === true) {
      // The name only through the caller's own view: a copy outside their scope stays unnamed.
      const { data: visible } = await supabase.from('documents' as never)
        .select('original_filename').eq('sha256_hash' as never, hash as never).limit(1);
      const name = ((visible ?? []) as Array<{ original_filename: string | null }>)[0]?.original_filename;
      throw new Error(`This exact file is already stored${name ? ` (as "${name}")` : ' on a record outside your area'}. Choose a different photo or file.`);
    }
  }

  const documentId = crypto.randomUUID();
  const path = keyFor(input.organizationId, input.ownerId, documentId, input.file.name);

  const { error: uploadError } = await supabase.storage.from(input.bucket).upload(path, input.file, {
    contentType: input.file.type || 'application/octet-stream',
    upsert: false,                       // storage has no UPDATE policy; an overwrite must fail
  });
  if (uploadError) {
    throw new Error(
      /exceeded the maximum allowed size/i.test(uploadError.message)
        ? 'That file is too large for this kind of document.'
        : /mime type|not supported/i.test(uploadError.message)
          ? 'That file type is not accepted here.'
          : /row-level security|Unauthorized/i.test(uploadError.message)
            ? 'Your role does not allow uploading this kind of document.'
            : describeError({ message: uploadError.message }),
    );
  }

  try {
    return await createRow<UploadedDocument>('documents', {
      id: documentId,
      document_type_id: input.documentTypeId ?? null,
      storage_bucket: input.bucket,
      storage_path: path,
      original_filename: input.file.name,
      mime_type: input.file.type || 'application/octet-stream',
      file_size_bytes: input.file.size,
      sha256_hash: hash,
      capture_method: input.captureMethod ?? 'upload',
      issued_date: input.issuedDate ?? null,
      expiry_date: input.expiryDate ?? null,
      verification_status: 'pending',
      status: 'active',
    });
  } catch (error) {
    // The row failed, so nothing references the file. Storage has no DELETE policy for a
    // browser session by design, so this is left for the retention job rather than silently
    // half-succeeding - and the user is told plainly.
    throw new Error(
      `${error instanceof Error ? error.message : 'The document could not be recorded.'} `
      + 'The file was uploaded but not attached; try again.',
    );
  }
}

/**
 * A URL that works for a few minutes. Never store one: it is a bearer token for that file, and
 * the whole point of a private bucket is that a leaked link stops working.
 */
export async function signedUrl(bucket: string, path: string, seconds = 300): Promise<string> {
  const { data, error } = await getSupabase().storage.from(bucket).createSignedUrl(path, seconds);
  if (error) throw new Error(describeError({ message: error.message }));
  return data.signedUrl;
}

/**
 * Short-lived URLs for several stored documents at once, keyed by document id - one read of
 * the document rows and one signing call per bucket, so a list of fifty plots does not make a
 * hundred requests to show fifty farmer photographs. A document the caller cannot read is simply
 * absent from the map (RLS hides its row; storage would refuse it anyway).
 */
export async function signedUrlsFor(documentIds: string[], seconds = 600): Promise<Record<string, string>> {
  const ids = [...new Set(documentIds.filter(Boolean))];
  if (ids.length === 0) return {};
  const { data, error } = await getSupabase().from('documents' as never)
    .select('id, storage_bucket, storage_path').in('id', ids as never);
  if (error) throw new Error(describeError(error));
  const rows = (data ?? []) as Array<{ id: string; storage_bucket: string; storage_path: string }>;

  const out: Record<string, string> = {};
  const byBucket = new Map<string, typeof rows>();
  for (const row of rows) byBucket.set(row.storage_bucket, [...(byBucket.get(row.storage_bucket) ?? []), row]);
  for (const [bucket, group] of byBucket) {
    const { data: signed } = await getSupabase().storage.from(bucket)
      .createSignedUrls(group.map((row) => row.storage_path), seconds);
    (signed ?? []).forEach((entry, index) => {
      const row = group[index];
      if (row && entry.signedUrl) out[row.id] = entry.signedUrl;
    });
  }
  return out;
}

export interface DocumentType {
  id: string;
  document_type_code: string;
  name: string;
  module_key: string | null;
  sensitive_class: string | null;
  validity_required: boolean | null;
}

export function documentTypes() {
  return listRows<DocumentType>('document_types', { orderBy: { column: 'name' } });
}
